import { Component, OnDestroy, OnInit, Pipe, PipeTransform, TemplateRef } from '@angular/core';
import { OrganizationService } from 'services/organization/organization.service';
import { ActivatedRoute, Router } from '@angular/router';
import { PublicRegistry } from 'services/organization/organization.interface';
import { BehaviorSubject, Observable } from 'rxjs';
import { skip, switchMap } from 'rxjs/operators';
import { SidebarService, SmartFilters, SmartFiltersService, SmartForm } from '@certemy/ui-components';
import { RegistryTableOrder } from 'components/registry-table/registry-table.component';
import { ComputedFrom } from '@certemy/common';
import _size from 'lodash-es/size';
import { ResizeService } from 'services/resize/resize.service';
import { findAndReplaceURL } from 'utils/helpers';
import { mapRegistries, PublicRegistryFilters } from 'pages/public-registry/public-registry.constants';
import _sortBy from 'lodash-es/sortBy';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
  selector: 'cm-public-registry-table',
  templateUrl: './public-registry-table.component.html',
  styleUrls: ['./public-registry-table.component.scss'],
})
export class PublicRegistryTableComponent implements OnInit, OnDestroy {
  constructor(
    private organizationService: OrganizationService,
    private route: ActivatedRoute,
    private resizeService: ResizeService,
    private router: Router,
    private sidebarService: SidebarService,
    private smartFiltersService: SmartFiltersService,
  ) {
    this.resizeService.resize$.pipe(untilDestroyed(this)).subscribe((props) => {
      this.isDesktop = props.isDesktop;
      this.setRegistries();
    });
  }
  registryId: string;
  count = 0;
  displayedCount = '';
  order: RegistryTableOrder = {
    id: 'last_name',
    type: 'asc',
  };
  isDesktop = true;
  registries: PublicRegistry.RegistryEntry[] = [];
  organization: PublicRegistry.OrganizationInfo = null;
  selectedLetter: string;
  selectedCredentialOption: SmartForm.FieldOption;
  filtersFilled: boolean;
  findAndReplaceLinks = findAndReplaceURL;
  boardList: SmartForm.FieldOption[] = [];
  boardLabel = 'Organization';
  selectedBoard: number;
  preview = false;

  filters$: BehaviorSubject<PublicRegistryFilters>;

  filtersValue$: Observable<SmartFilters.FiltersValue> = this.smartFiltersService.value$;
  private _smartFiltersValue: any = {};

  noContentConfig = {
    src: '/assets/images/public-registry/no-results/no-results.png',
    srcset: `/assets/images/public-registry/no-results/no-results.png 1x,
                 /assets/images/public-registry/no-results/no-results@2x.png 2x,
                 /assets/images/public-registry/no-results/no-results@3x.png 3x`,
    title: 'No results',
    subtitle: 'There are no credentials to be shown',
  };

  ngOnInit() {
    this.initFilters();
    const [{ result, count, organization }, boardList, registryId] = this.route.snapshot.data.data;
    this.preview = this.router.url.includes('/public-registry/preview');
    this.registryId = registryId;
    this.boardLabel = organization.boardLabel || 'Organization';
    this.boardList = _sortBy(boardList, ['name']);
    this.organization = organization;
    this.setRegistries(result);
    this.count = count;
    this.updateDisplayedCount();
    this.selectedCredentialOption = { name: '', id: null };
    this.initializeSidebarFilter();
  }

  ngOnDestroy() {}

  @ComputedFrom('organization', 'boardList')
  get showBoards(): boolean {
    return this.organization && this.organization.isParentRegistry && !!this.boardList.length;
  }

  private initializeSidebarFilter() {
    this.smartFiltersService.initialize([
      new SmartFilters.SelectFilter({
        name: 'organizationId',
        hidden: !this.showBoards,
        label: this.boardLabel,
        options: _sortBy(this.boardList, ['name']),
      }),
      new SmartFilters.TypeaheadFilter({
        name: 'credential',
        label: 'Credential',
        hidden: this.showBoards,
        request$: this.onCredentialSearch,
        formatter: this.credentialFormatter,
        placeholder: 'Select',
        disabled: this.showBoards,
      }),
    ]);

    this.filtersValue$.pipe(skip(1), untilDestroyed(this)).subscribe((filters: any) => {
      const { organizationId: oldOrganizationId } = this._smartFiltersValue;
      const { organizationId: newOrganizationId, credential: newCredential } = filters;

      const newFilters = this.createNewFilters(newOrganizationId, newCredential);

      this._smartFiltersValue = filters;

      this.selectedBoard = newOrganizationId;
      if (newCredential) {
        const { id, name } = newCredential;
        this.selectedCredentialOption = { id, name };
      } else {
        this.selectedCredentialOption = null;
      }

      if (this.showBoards) {
        if (!this.isDesktop) {
          const credentialFilter = this.smartFiltersService.config$.value.find(({ name }) => name === 'credential');
          credentialFilter.disabled = !newOrganizationId;
        }
        if (newOrganizationId !== oldOrganizationId && newCredential) {
          if (!this.isDesktop) {
            this.updateSmartFiltersValue({
              credential: null,
            });
          } else {
            this.smartFiltersService.filtersChange({
              name: 'credential',
              value: null,
            });
            return;
          }
        }
      }

      this.nextFilters((_filters) => ({
        ..._filters,
        page: 1,
        filters: {
          ..._filters.filters,
          ...newFilters,
        },
      }));
    });
  }

  private createNewFilters(newOrganizationId: number, newCredential?: PublicRegistry.Credential) {
    const getCredentialValue = (credential: PublicRegistry.Credential, key: string) =>
      credential ? credential[key] : [];

    return {
      organizationId: newOrganizationId,
      certificationIds: getCredentialValue(newCredential, 'certificationIds'),
      parentVersionIds: getCredentialValue(newCredential, 'parentVersionIds'),
      acronyms: getCredentialValue(newCredential, 'acronyms'),
    };
  }

  private updateSmartFiltersValue(newValues: SmartFilters.FiltersValue) {
    setTimeout(() => {
      this.smartFiltersService.value$.next({
        ...this.smartFiltersService.value$.value,
        ...newValues,
      });
    });
  }

  private initFilters() {
    const baseFilterParam = new PublicRegistryFilters();
    this.filters$ = new BehaviorSubject(baseFilterParam);

    this.filters$
      .pipe(
        skip(1),
        switchMap((filters: PublicRegistryFilters) => {
          if (this.preview) {
            return this.organizationService.getPreviewOrganizationRegistry(this.registryId, filters);
          }
          return this.organizationService.getPublicOrganizationRegistry(this.registryId, filters);
        }),
        untilDestroyed(this),
      )
      .subscribe(({ result, count }) => {
        this.setRegistries(result);
        this.count = count;
        const { certificationIds, organizationId } = this.filters$.getValue().filters;
        this.filtersFilled = _size(certificationIds) || organizationId;
        this.updateDisplayedCount();
      });
  }

  private setRegistries(arr?: PublicRegistry.RegistryEntry[]) {
    this.registries = mapRegistries(arr || this.registries, this.isDesktop);
  }

  private nextFilters(reduce: (filters: PublicRegistryFilters) => PublicRegistryFilters) {
    const currentFilters = this.filters$.getValue();

    this.filters$.next(reduce(currentFilters));
  }

  private updateDisplayedCount() {
    const currentPage = this.filters$.getValue().page;
    const pageSize = this.filters$.getValue().page_size;
    const from = (currentPage - 1) * pageSize + 1;
    const numberOfPages = Math.ceil(this.count / pageSize);
    const to = currentPage < numberOfPages ? currentPage * pageSize : this.count;

    this.displayedCount = this.count ? `Displayed ${from}-${to} of ${this.count}` : '';
  }

  selectFilter(param, value: PublicRegistry.Credential | number) {
    this.smartFiltersService.filtersChange({
      name: param,
      value: value as SmartFilters.FilterValue,
    });
  }

  resetFilters() {
    this.updateSmartFiltersValue({
      credential: null,
      organizationId: null,
    });
  }

  onSearchChange(search: string) {
    this.nextFilters((filters) => ({
      ...filters,
      page: 1,
      search,
    }));
  }

  onPageChange(page: number) {
    this.nextFilters((filters) => ({
      ...filters,
      page,
    }));
  }

  onSelectedLetter(letter?: string) {
    if (letter !== this.selectedLetter) {
      const letterFilters = { last_name: letter ? [letter] : [] };
      this.nextFilters((filters) => ({
        ...filters,
        page: 1,
        filters: {
          ...filters.filters,
          ...letterFilters,
        },
      }));
      this.selectedLetter = letter;
    }
  }

  onViewDetails(data) {
    this.router.navigate([`${data.id}`], { relativeTo: this.route.parent });
  }

  onOrderChange(newOrder: { id: string; type: string }) {
    this.order = newOrder;
    this.nextFilters((filters) => ({
      ...filters,
      page: 1,
      order_id: this.order.id,
      order_type: this.order.type,
    }));
  }

  credentialFormatter(option: SmartForm.FieldOption): string {
    return option.name;
  }

  onCredentialSearch = (search: string): Observable<PublicRegistry.Credential[]> => {
    const filters = {
      boardId: this.selectedBoard,
    };
    return this.organizationService
      .getPublicRegistryCredentials(this.registryId, { search, limit: 50, filters })
      .pipe(untilDestroyed(this));
  };

  openSideBar(templateRef: TemplateRef<any>) {
    this.sidebarService
      .open(templateRef, {
        width: '100%',
      })
      .pipe(untilDestroyed(this))
      .subscribe();
  }
  closeSideBar() {
    this.sidebarService.close();
  }
  onResetFilters() {
    this.smartFiltersService.resetFilters();
  }
}

@Pipe({
  name: 'hiddenFilterPipe',
})
export class ColumnHiddenFilterPipe implements PipeTransform {
  transform(arr = [], ignoreFilter = false): any[] {
    if (ignoreFilter) {
      return arr;
    }
    return arr.filter((column) => !column.hidden);
  }
}
